import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  Tooltip,
  FormControlLabel,
  Switch
} from '@mui/material';

import {
  EMAIL_TEMPLATE_STATUS_IDS,
  SERVICE_IDS,
  TEMPLATE_USER_TYPES_IDS,
  ZERO_GUID,
  API_REQUEST_TYPES_IDS
} from 'modules/enumerations/enumerationIds';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
import { EmailEditorTiny } from '../emailEditors/EmailEditorTiny';

import { createFilteredRegardingOptions } from './adminEmailTemplateView.container';
const AdminEmailTemplateDetails = props => {
  const { enumerations, template, updateTemplate, tokens, changeStatus, versionOptions, history, isReadOnly } = props;
  const [activeEditor, setActiveEditor] = useState(null);

  const subjectEditorRef = useRef(null);
  const bodyEditorRef = useRef(null);

  const authCommentServiceTypes = enumerations?.authCommentService ? enumerations.authCommentService.filter(s => s.enumType === 1).sort((a, b) =>
    a.title.localeCompare(b.title)
  ) : [];

  const authCommentRegardingTypes =
    enumerations?.authCommentRegarding && enumerations.authCommentRegarding.length > 0
      ? enumerations.authCommentRegarding.filter(s => s.isActive === true)
      : [{ id: ZERO_GUID, title: '' }];

  const templateUserTypes =
    enumerations?.templateUserTypes && enumerations.templateUserTypes.length > 0
      ? enumerations.templateUserTypes.filter(s => s.isActive === true)
      : [{ id: ZERO_GUID, title: '' }];

  const apiRequestTypes =
    enumerations?.apiRequestTypes && enumerations.apiRequestTypes.length > 0
      ? enumerations.apiRequestTypes.filter(s => s.isActive === true)
      : [{ id: ZERO_GUID, title: '' }];

  const mresServiceSelection =
    enumerations?.authCommentService && enumerations.authCommentService.length > 0
      ? enumerations.authCommentService.filter(
        s => s.isActive === true && (s.id === SERVICE_IDS.DESTINATION_BUYER_SERVICE_ID || s.id === SERVICE_IDS.HOME_SALE_SERVICE_ID)
      )
      : [{ id: ZERO_GUID, title: '' }];

  const [localTemplate, setLocalTemplate] = useState(
    template || {
      templateId: 0,
      templateName: '',
      subjectTemplate: '',
      bodyHtmlTemplate: '',
      serviceId: ZERO_GUID,
      regardingId: ZERO_GUID,
      templateUserTypeId: TEMPLATE_USER_TYPES_IDS.TRANSFEREE,
      isSingleRequest: false,
      isServiceGeneralTemplate: false,
      versionNumber: 1,
      versionStatusId: EMAIL_TEMPLATE_STATUS_IDS.DRAFT,
      parentEmailTemplateId: null,
      isMorrealeHomesaleBrokerListRequest: false,
      isMorrealeDestinationBuyerHomeFindingRequest: false
    }
  );

  const [filteredRegardingOptions, setFilteredRegardingOptions] = useState(createFilteredRegardingOptions(localTemplate, authCommentRegardingTypes));

  const displayTokens = Array.isArray(tokens) ? tokens : [];

  const groupedTokens = useMemo(() => {
    const groups = {};

    displayTokens.forEach(tok => {
      const groupName = tok.tokenModelName || 'Other';
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(tok);
    });

    const sortedEntries = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
    sortedEntries.forEach(([, list]) => {
      list.sort((a, b) => a.tokenName.localeCompare(b.tokenName));
    });

    return sortedEntries;
  }, [displayTokens]);

  const handleInsertToken = tokenName => {
    const tokenText = `{{${tokenName}}}`;
    if (activeEditor === 'subject' && subjectEditorRef.current) {
      const input = subjectEditorRef.current;
      const start = input.selectionStart ?? (localTemplate.subjectTemplate || '').length;
      const end = input.selectionEnd ?? start;
      const current = localTemplate.subjectTemplate || '';
      const next = current.slice(0, start) + tokenText + current.slice(end);

      handleChangeField('subjectTemplate', next);
      requestAnimationFrame(() => {
        try {
          input.focus();
          const cursor = start + tokenText.length;
          input.setSelectionRange(cursor, cursor);
        } catch { }
      });

      return;
    }

    if (activeEditor === 'body' && bodyEditorRef.current?.insertTokenAtCursor) {
      bodyEditorRef.current.insertTokenAtCursor(tokenText);
      return;
    }
  };

  const showIsGeneralTemplate = () => {
    return false;
  };

  useEffect(() => {
    if (template) {
      setLocalTemplate(prev => ({
        ...prev,
        templateId: template.templateId ?? prev.templateId,
        templateName: template.templateName ?? prev.templateName,
        subjectTemplate: template.subjectTemplate ?? prev.subjectTemplate,
        bodyHtmlTemplate: template.bodyHtmlTemplate ?? prev.bodyHtmlTemplate,
        serviceId: template.serviceId ?? prev.serviceId,
        regardingId: template.regardingId ?? prev.regardingId,
        templateUserTypeId: template.templateUserTypeId ?? prev.templateUserTypeId,
        isSingleRequest: typeof template.isSingleRequest === 'boolean' ? template.isSingleRequest : prev.isSingleRequest,
        isServiceGeneralTemplate: false,
        //typeof template.isServiceGeneralTemplate === 'boolean' ? template.isServiceGeneralTemplate : prev.isServiceGeneralTemplate,
        isApiRequest: typeof template.isApiRequest === 'boolean' ? template.isApiRequest : prev.isApiRequest,
        apiRequestTypeId: template.apiRequestTypeId ?? prev.apiRequestTypeId,
        apiRequestUrl: template.apiRequestUrl ?? prev.apiRequestUrl,
        versionNumber: template.versionNumber ?? prev.versionNumber,
        versionStatusId: template.versionStatusId ?? prev.versionStatusId,
        parentEmailTemplateId:
          template.parentEmailTemplateId === null || template.parentEmailTemplateId === undefined ? null : template.parentEmailTemplateId,
        isMorrealeHomesaleBrokerListRequest: template.isMorrealeHomesaleBrokerListRequest ?? prev.isMorrealeHomesaleBrokerListRequest,
        isMorrealeDestinationBuyerHomeFindingRequest:
          template.isMorrealeDestinationBuyerHomeFindingRequest ?? prev.isMorrealeDestinationBuyerHomeFindingRequest
      }));
    }
  }, [template]);

  useEffect(() => {
    if (!isIdEqual(localTemplate.templateUserTypeId, TEMPLATE_USER_TYPES_IDS.VENDOR)) {
      setLocalTemplate(prev => ({
        ...prev,
        apiRequestTypeId: ZERO_GUID
      }));
    }
  }, [localTemplate.templateUserTypeId]);

  const handleChangeField = (field, value) => {
    if (localTemplate[field] === value) return;

    const updated = {
      ...localTemplate,
      [field]: value
    };
    setLocalTemplate(updated);
  };

  const handleServiceChange = (serviceId) => {
    setLocalTemplate(prev => ({
      ...prev,
      serviceId: serviceId
    }));
    setFilteredRegardingOptions(createFilteredRegardingOptions({ ...localTemplate, serviceId: serviceId }, authCommentRegardingTypes));
  };

  const handleSave = () => {
    if (!updateTemplate) return;
    //Leave these false. Using Service Id for now.
    localTemplate.isMorrealeHomesaleBrokerListRequest = false;
    localTemplate.isMorrealeDestinationBuyerHomeFindingRequest = false;

    updateTemplate(localTemplate);
  };

  // const handleDelete = () => {
  //   if (!deleteTemplate) return;
  //   const confirmed = window.confirm('Are you sure you want to hide this template from the list?');
  //   if (confirmed) {
  //     deleteTemplate(localTemplate);
  //   }
  // };

  const handleStatusChange = () => {
    if (!changeStatus) return;
    changeStatus(localTemplate);
  };

  return (
    <Box sx={{ padding: '24px' }}>
      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5} lg={6}>
              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                Template Name
              </Typography>
              <TextField
                required
                fullWidth
                size="small"
                variant="outlined"
                value={localTemplate.templateName || ''}
                onChange={e => handleChangeField('templateName', e.target.value)}
                disabled={isReadOnly}
              />
            </Grid>
            <Grid item xs={12} md={2} lg={2} sx={{ mt: '27px' }}>
              <FormControl size="small" variant="standard" sx={{ minWidth: 220 }}>
                <InputLabel id="version-select-label">Version</InputLabel>
                <Select
                  labelId="version-select-label"
                  value={localTemplate.templateId}
                  onChange={e => history.push(`/templates/${e.target.value}`)}
                >
                  {versionOptions.map(v => (
                    <MenuItem key={v.templateId} value={v.templateId}>
                      {`v${v.versionNumber} • ${isIdEqual(v.versionStatusId, ZERO_GUID) || isIdEqual(v.versionStatusId, EMAIL_TEMPLATE_STATUS_IDS.DRAFT)
                        ? 'Draft'
                        : isIdEqual(v.versionStatusId, EMAIL_TEMPLATE_STATUS_IDS.ACTIVE)
                          ? 'Active'
                          : 'Expired'
                        }`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              md={5}
              lg={4}
              sx={{ mt: '30px', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', gap: 2 }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#41B7C0',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#3d959c' },
                  minWidth: '120px',
                  minHeight: '30px'
                }}
                disabled={isReadOnly}
                onClick={handleSave}
              >
                Save
              </Button>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#5D9878',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#528569' },
                  minWidth: '120px',
                  minHeight: '30px'
                }}
                onClick={handleStatusChange}
                disabled={
                  isIdEqual(localTemplate.versionStatusId, EMAIL_TEMPLATE_STATUS_IDS.CANCELLED)
                }
              >
                {isIdEqual(localTemplate.versionStatusId, EMAIL_TEMPLATE_STATUS_IDS.EXPIRED)
                  ? 'Reactivate'
                  : isIdEqual(localTemplate.versionStatusId, EMAIL_TEMPLATE_STATUS_IDS.DRAFT) ||
                    isIdEqual(localTemplate.versionStatusId, ZERO_GUID)
                    ? 'Make Active'
                    : 'Disable'}
              </Button>
              {/* <Button
                variant="contained"
                sx={{
                  backgroundColor: '#868686',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#6d6d6d' },
                  minWidth: '120px',
                  minHeight: '30px'
                }}
                disabled={isReadOnly}
                onClick={handleDelete}
              >
                Hide
              </Button> */}
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {isIdEqual(localTemplate.apiRequestTypeId, API_REQUEST_TYPES_IDS.MORREALE) ? (
                  <Grid item xs={12} md={2}>
                    <FormControl sx={{ mt: 1 }} fullWidth size="small" variant="standard">
                      <InputLabel id="template-service-label">Service</InputLabel>
                      <Select
                        labelId="template-service-label"
                        label="Service"
                        value={localTemplate.serviceId || ZERO_GUID}
                        onChange={e => handleChangeField('serviceId', e.target.value)}
                        disabled={isReadOnly}
                      >
                        {mresServiceSelection && mresServiceSelection.length > 0 ? (
                          mresServiceSelection.map(opt => (
                            <MenuItem key={opt.id} value={opt.id}>
                              {opt.title}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem>{'No options available'}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                ) : (
                  <Grid item xs={12} md={2}>
                    <FormControl sx={{ mt: 1 }} fullWidth size="small" variant="standard">
                      <InputLabel id="template-service-label">Service</InputLabel>
                      <Select
                        labelId="template-service-label"
                        label="Service"
                        value={localTemplate.serviceId || ZERO_GUID}
                        onChange={e => handleServiceChange(e.target.value)}
                        disabled={isReadOnly}
                      >
                        {authCommentServiceTypes && authCommentServiceTypes.length > 0 ? (
                          authCommentServiceTypes.map(opt => (
                            <MenuItem key={opt.id} value={opt.id}>
                              {opt.title}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem>{'No options available'}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} md={2}>
                  <FormControl sx={{ mt: 1 }} fullWidth size="small" variant="standard">
                    <InputLabel id="template-regarding-label">Regarding</InputLabel>
                    <Select
                      labelId="template-regarding-label"
                      label="Regarding"
                      value={localTemplate.regardingId || ZERO_GUID}
                      onChange={e => handleChangeField('regardingId', e.target.value)}
                      disabled={isReadOnly}
                    >
                      {filteredRegardingOptions && filteredRegardingOptions.length > 0 ? (
                        filteredRegardingOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.title}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem>{'No options available'}</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl sx={{ mt: 1 }} fullWidth size="small" variant="standard">
                    <InputLabel id="template-recipient-type-label">Recipient Type</InputLabel>
                    <Select
                      labelId="template-recipient-type-label"
                      label="Recipient Type"
                      value={
                        localTemplate.templateUserTypeId && localTemplate.templateUserTypeId !== ZERO_GUID
                          ? localTemplate.templateUserTypeId
                          : TEMPLATE_USER_TYPES_IDS.TRANSFEREE
                      }
                      disabled={isReadOnly}
                      onChange={e => handleChangeField('templateUserTypeId', e.target.value)}
                    >
                      {templateUserTypes && templateUserTypes.length > 0 ? (
                        templateUserTypes.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.title}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem>{'No options available'}</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                {localTemplate.templateUserTypeId === TEMPLATE_USER_TYPES_IDS.VENDOR && (
                  <>
                    <Grid item xs={12} md={3}>
                      <FormControl sx={{ mt: 1 }} fullWidth size="small" variant="standard">
                        <InputLabel id="template-recipient-type-label">Vendor</InputLabel>
                        <Select
                          labelId="template-recipient-type-label"
                          label="Recipient Type"
                          value={
                            localTemplate.apiRequestTypeId && localTemplate.apiRequestTypeId !== ZERO_GUID
                              ? localTemplate.apiRequestTypeId
                              : ZERO_GUID
                          }
                          disabled={isReadOnly}
                          onChange={e => handleChangeField('apiRequestTypeId', e.target.value)}
                        >
                          {apiRequestTypes && apiRequestTypes.length > 0 ? (
                            apiRequestTypes.map(opt => (
                              <MenuItem key={opt.id} value={opt.id}>
                                {opt.title}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem>{'No options available'}</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!localTemplate.isSingleRequest}
                        onChange={e => handleChangeField('isSingleRequest', e.target.checked)}
                        color="primary"
                        disabled={isReadOnly}
                      />
                    }
                    label="Single Send Only"
                  />
                </Grid>
                {showIsGeneralTemplate() && (
                  <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!localTemplate.isServiceGeneralTemplate}
                          onChange={e => handleChangeField('isServiceGeneralTemplate', e.target.checked)}
                          color="primary"
                          disabled={isReadOnly}
                        />
                      }
                      label="Service General Template"
                    />
                  </Grid>
                )}
                {/* <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!localTemplate.isApiRequest}
                        onChange={e => handleChangeField('isApiRequest', e.target.checked)}
                        color="primary"
                        disabled={isReadOnly}
                      />
                    }
                    label="Is API Request"
                  />
                </Grid>
                {localTemplate.isApiRequest && (
                  <>
                    <Grid item xs={12} md={3}>
                      <FormControl sx={{ mt: 1 }} fullWidth size="small" variant="standard">
                        <InputLabel id="template-recipient-type-label">API Request Type</InputLabel>
                        <Select
                          labelId="template-recipient-type-label"
                          label="Recipient Type"
                          value={
                            localTemplate.apiRequestTypeId && localTemplate.apiRequestTypeId !== ZERO_GUID
                              ? localTemplate.apiRequestTypeId
                              : ZERO_GUID
                          }
                          disabled={isReadOnly}
                          onChange={e => handleChangeField('apiRequestTypeId', e.target.value)}
                        >
                          {apiRequestTypes && apiRequestTypes.length > 0 ? (
                            apiRequestTypes.map(opt => (
                              <MenuItem key={opt.id} value={opt.id}>
                                {opt.title}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem>{'No options available'}</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                        API Request URL
                      </Typography>
                      <TextField
                        required
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={localTemplate.apiRequestUrl || ''}
                        disabled={isReadOnly}
                        onChange={e => handleChangeField('apiRequestUrl', e.target.value)}
                      />
                    </Grid>
                  </>
                )} */}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={3} lg={3}>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              padding: '12px',
              maxHeight: '70vh',
              overflowY: 'auto',
              bgcolor: '#fafafa'
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Available Tokens
            </Typography>
            {displayTokens.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                No tokens available yet. Once the token system is wired up, they’ll show up here.
              </Typography>
            )}

            {displayTokens.length > 0 &&
              groupedTokens.map(([groupName, tokenList]) => (
                <Box key={groupName} sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 0.5,
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}
                  >
                    {groupName}
                  </Typography>

                  {tokenList.map(tok => (
                    <Tooltip
                      key={tok.tokenName}
                      title={
                        <Box>
                          {tok.description && (
                            <Typography variant="caption" display="block">
                              {tok.description}
                            </Typography>
                          )}
                          {tok.valueType && (
                            <Typography variant="caption" display="block">
                              Type: {tok.valueType}
                            </Typography>
                          )}
                        </Box>
                      }
                      placement="right"
                      arrow
                    >
                      <Box
                        onMouseDown={e => {
                          e.preventDefault();
                          handleInsertToken(tok.tokenName);
                        }}
                        sx={{
                          borderRadius: '4px',
                          p: 0.75,
                          mb: 0.75,
                          backgroundColor: 'white',
                          border: '1px solid #e0e0e0',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#f0f7ff',
                            borderColor: '#41B7C0'
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {`{{${tok.tokenName}}}`}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              ))}
          </Box>
        </Grid>

        <Grid item xs={12} md={9} lg={9}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                Subject
              </Typography>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                value={localTemplate.subjectTemplate || ''}
                onChange={e => handleChangeField('subjectTemplate', e.target.value)}
                inputRef={subjectEditorRef}
                onFocus={() => setActiveEditor('subject')}
                onBlur={() => setActiveEditor(null)}
                disabled={isReadOnly}
              />
            </Box>
            <Box sx={{ flex: 1, minHeight: '300px' }}>
              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                Body
              </Typography>
              <EmailEditorTiny
                ref={bodyEditorRef}
                initialHtml={localTemplate.bodyHtmlTemplate || ''}
                onHtmlChange={html => handleChangeField('bodyHtmlTemplate', html)}
                onFocus={() => setActiveEditor('body')}
                onBlur={() => setActiveEditor(null)}
                disabled={isReadOnly}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminEmailTemplateDetails;
